<template>
  <v-menu
    ref="chainMenu"
    :close-on-content-click="false"
    offset-y
    left
    transition="slide-y-transition"
  >
    <template #activator="{ on }">
      <v-btn
        text
        :icon="$vuetify.breakpoint.smAndDown"
        v-on="on"
        v-show="hasHotelChains"
        :class="{'btn-40': $vuetify.breakpoint.smAndDown}"
      >
        <span
          class="text-truncate d-md-inline d-sm-none chain-name-text"
          v-show="$vuetify.breakpoint.mdAndUp"
          :class="$vuetify.rtl ? 'mr-1' : 'ml-1'"
        >{{ selectedChainName }}</span>
        <v-icon
          class="d-sm-inline d-md-none"
        >
          mdi-office-building-cog
        </v-icon>
        <v-icon
          right
          class="d-md-inline d-sm-none"
          v-show="$vuetify.breakpoint.mdAndUp"
        >
          mdi-chevron-down
        </v-icon>
      </v-btn>
    </template>
    <v-card
      max-height="400px"
      width="300px"
      class="chain-menu-card"
    >
      <v-text-field
        v-if="showSearch"
        v-model="searchKeyword"
        placeholder="Filter chain..."
        autofocus
        flat
        hide-details
        rounded
        prepend-inner-icon="mdi-magnify"
        class="mx-1 mb-1"
      />
      <v-list
        dense
        nav
        class="chain-menu-list"
      >
        <v-list-item
          @click="selectChain(null)"
          :class="{ 'v-list-item--active': selectedHotelChainId === null }"
        >
          <v-list-item-title>
            <span class="all-chains-text">All Chains</span>
          </v-list-item-title>
        </v-list-item>
        <v-list-item
          v-for="chain in filteredChains"
          :key="chain.id"
          @click="selectChain(chain)"
          :class="{ 'v-list-item--active': selectedHotelChainId === chain.id }"
        >
          <v-list-item-title>
            {{ chain.name }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import qs from "query-string";
import Auth from "@/services/Auth";
import { Api } from "@/services/Api";
import { HotelChain } from "@/models/HotelChain";
import { Hotel } from "@/models/Hotel";

@Component
export default class ToolbarHotelChains extends Vue {
  searchKeyword: string = "";

  async mounted() {
    this.getHotelChains();
  }

  getHotelChains() {
    this.$store.dispatch("app/getHotelChains");
  }

  get showSearch() {
    return this.hotelChains && this.hotelChains.length > 5;
  }

  get state() {
    return this.$store.state.app;
  }

  get hotelChains() {
    return this.state.hotelChains || [];
  }

  get hasHotelChains() {
    return this.hotelChains && this.hotelChains.length > 0;
  }

  get filteredChains() {
    if (!this.hotelChains) return [];

    return this.hotelChains.filter((chain) => {
      return chain?.name
        ?.toLowerCase()
        ?.includes(this.searchKeyword?.toLowerCase());
    });
  }

  get selectedHotelChainId() {
    return this.state.selectedHotelChainId;
  }

  get selectedChainName() {
    if (this.selectedHotelChainId === null) {
      return "All Chains";
    }

    const selectedChain = this.hotelChains.find(chain => chain.id === this.selectedHotelChainId);
    return selectedChain ? selectedChain.name : "All Chains";
  }

  selectChain(chain: HotelChain | null): void {
    const chainId = chain ? chain.id : null;
    const previousChainId = this.$store.state.app.selectedHotelChainId;

    // If clicking the same chain that's already selected, just close the menu
    if (chainId === previousChainId) {
      const chainMenuRef = this.$refs.chainMenu as any;
      if (chainMenuRef) {
        chainMenuRef.isActive = false;
      }
      return;
    }

    // Update the selected chain
    this.$store.commit("app/setSelectedHotelChainId", chainId);

    // Close the chain dropdown menu
    const chainMenuRef = this.$refs.chainMenu as any;
    if (chainMenuRef) {
      chainMenuRef.isActive = false;
    }

    // Get current selected hotel
    const currentHotel = this.$store.state.app.hotel;
    const currentHotelId = currentHotel?.id;

    // If selecting "All Chains", don't reload - just update the filter
    if (chainId === null) {
      return;
    }

    // Check if current hotel belongs to the newly selected chain
    if (currentHotel && currentHotel.hotel_chain_id === chainId) {
      // Current hotel is already in this chain - no need to reload
      return;
    }

    // Current hotel is NOT in the selected chain, need to find a new hotel
    const hotels = this.$store.state.app.hotels || [];
    const chainHotels = hotels.filter(h => h.hotel_chain_id === chainId);

    if (chainHotels.length > 0) {
      // First, try to find a default hotel for this chain
      const defaultHotel = chainHotels.find(h => h.default_hotel === true);

      if (defaultHotel) {
        // Select the default hotel and update the page
        this.setHotelAndReload(defaultHotel);
      } else if (chainHotels.length === 1) {
        // If there's only one hotel in this chain, select it automatically
        this.setHotelAndReload(chainHotels[0]);
      } else {
        // Multiple hotels and no default - select the first one
        this.setHotelAndReload(chainHotels[0]);
      }
    } else {
      // No hotels in this chain - show error and reset to "All Chains"
      const chainName = chain?.name || "this chain";
      this.$archNotify({
        message: `No hotels found in ${chainName}. Please select a different chain.`,
        color: "warning"
      });
      // Reset back to "All Chains" to prevent user from getting stuck
      this.$store.commit("app/setSelectedHotelChainId", null);
    }
  }

  async autoAssignOutlet(hotel: Hotel): Promise<any> {
    const token = await Auth.GetTokenPayload();
    const outletsResponse = await Api.get(`/my/outlets/${token?.uid}/v1?hotelId=${hotel.id}`, null, { maxAgeInSecond: 0 });
    const outlets: any[] = outletsResponse.data.Data || [];
    let autoAssignOutlet: boolean = false;
    const query: any = this.$route.query;
    query.hotelId = hotel.id + "";

    // Check if there is outlet assigned in query string
    if (outlets.length > 0 && (typeof query.outletId == "undefined" || query.outletId == "")) {
      autoAssignOutlet = true;
    } else if (outlets.filter(r => r.id == query.outletId).length == 0) {
      autoAssignOutlet = true;
    }

    if (autoAssignOutlet) {
      // Find default outlet
      const outlet = outlets.find(r => r.default_outlet == true);
      if (typeof outlet != "undefined") {
        query.outletId = outlet.id + "";
      } else {
        if (typeof outlets[0] != "undefined") {
          query.outletId = outlets[0].id + "";
        } else {
          // Means that hotel doesn't have any outlet
          delete query.outletId;
        }
      }
    }

    return query;
  }

  async setHotelAndReload(hotel: Hotel): Promise<void> {
    // Update store first
    this.$store.commit("app/setSelectedHotel", hotel);

    // Auto-assign appropriate outlet for this hotel (same logic as ToolbarHotels)
    const query: any = await this.autoAssignOutlet(hotel);
    const location: string = this.$route.path + "?" + qs.stringify(query);
    window.location.href = location;
  }
}
</script>

<style scoped lang="scss">
.chain-menu-card {
  background: white;
  padding: 10px 0;
  border: solid 1px #d6d0d0 !important;
}

.chain-menu-list {
  max-height: 300px;
  overflow: auto;
}

.chain-name-text {
  max-width: 180px;
}

.all-chains-text {
  font-style: italic;
  color: #666;
}
</style>
